import * as React from "react";
import { useAccount, State } from "@grandvisionhq/auth";
import { Context, Models } from "@grandvisionhq/carts-v2";
import { toast, Loader } from "@grandvisionhq/common";
import { Util } from "@grandvisionhq/forms";
import { useLink } from "@grandvisionhq/frk-routing";
import { useSync } from "@grandvisionhq/lib-utils-w3c";
import { Hooks, PaymentServiceProvider, PaymentMethod } from "@grandvisionhq/payments";
import { useDispatch, useIntl, useSelector, useFeatureToggles } from "@grandvisionhq/state";
import { init } from "../../actions.js";
import { pushCheckoutEventToDataLayer, pushExpressCheckoutSubmitEventToDataLayer } from "../../data-layer.js";
import { useSetCustomerDetails } from "../../hooks/operations/hooks/operations/use-set-customer-details.js";
import { useCreateOrder } from "../../hooks/orders/hooks/orders/use-create-order.js";
import { useCreateOrderDraft } from "../../hooks/orders/hooks/orders/use-create-order-draft.js";
import { useRedirectToOrderComplete } from "../../hooks/orders/hooks/orders/use-redirect-to-order-confirm-page.js";
import { mapPaymentDataToCustomerDetails } from "../../mappers/data/mappers/data/map-payment-data-to-customer-details.js";
import { mapPaymentDataToSummaries } from "../../mappers/view/mappers/view/map-payment-data-to-summaries.js";
import { Header } from "../../modules/Header/modules/Header/Header.js";
import { getCheckout, isInitialized } from "../../selectors.js";
import { toggleExpressCheckout, markFieldsTouched } from "../../StepShipping/StepShipping/actions.js";
import { getShippingDetails } from "../../StepShipping/StepShipping/selectors.js";
import { ExpressOverview } from "./ExpressOverview.js";
import { useGetExpressLabels } from "../hooks/ExpressOverview/hooks/use-get-express-labels.js";
import { useSetDefaultShippingMethod } from "../hooks/ExpressOverview/hooks/use-set-default-shipping-method.js";
/* empty css                              */
import { CheckoutErrorsMapping } from "../../mappers/mappers/errors-mapping.js";
import { getShippingMethodInfo } from "../../utils/utils/get-shipping-method-info.js";
const ExpressOverviewContainer = (props) => {
  const { type, backButton, referenceId, paymentToken, shippingMethodsAsides } = props;
  const dispatch = useDispatch();
  const { currency, country, locale } = useIntl();
  const { loading, cart } = Context.useCart();
  const { onClick: navigateToCart } = useLink({ route: "cart.overview" }, { replace: true }) || {};
  const broadCastChannel = useSync("new-cart-created").getChannel();
  const shippingDetails = useSelector(getShippingDetails);
  broadCastChannel.onmessage = () => {
    toast.failed("cart.errors.cartNotInActiveState");
    navigateToCart?.();
  };
  const checkoutState = useSelector(getCheckout);
  const redirectToOrderComplete = useRedirectToOrderComplete();
  const { setCustomerDetails, isUpdating: isCustomerDetailsUpdating } = useSetCustomerDetails();
  const createOrderDraft = useCreateOrderDraft();
  const [createOrder, { loading: isOrderUpdating }] = useCreateOrder();
  const { setDefaultShippingMethod, areShippingMethodsLoading } = useSetDefaultShippingMethod();
  const { featureConfigs, isMyAccountEnabled } = useFeatureToggles();
  const { expressDefaultShippingMethod } = featureConfigs;
  const authState = useAccount();
  const isLoggedIn = isMyAccountEnabled && authState === State.AuthState.LoggedIn;
  const { labels, summaryLabels } = useGetExpressLabels(type);
  const { getPaymentDataByToken, data, isPaymentDataLoading } = Hooks.useGetPaymentDataByToken({
    onError: () => {
      toast.failed(CheckoutErrorsMapping.SomethingWentWrong);
      navigateToCart?.();
    }
  });
  const { addresses, contactDetails } = mapPaymentDataToSummaries(data);
  const dobFormRef = React.useRef(null);
  React.useEffect(() => {
    if (!isInitialized({ checkout: checkoutState })) {
      dispatch(init());
    }
  }, [dispatch, checkoutState]);
  React.useEffect(() => {
    const isFormEmpty = Object.keys(shippingDetails?.expressCheckout.formData ?? {}).length === 0;
    if (!isFormEmpty) {
      dispatch(
        toggleExpressCheckout({
          enabled: true
        })
      );
    }
    return () => {
      dispatch(
        toggleExpressCheckout({
          enabled: false
        })
      );
    };
  }, [dispatch, shippingDetails?.expressCheckout.formData]);
  React.useEffect(() => {
    if (paymentToken) {
      getPaymentDataByToken(paymentToken);
    }
  }, [paymentToken]);
  React.useEffect(() => {
    if (expressDefaultShippingMethod && !areShippingMethodsLoading && expressDefaultShippingMethod.length) {
      setDefaultShippingMethod(expressDefaultShippingMethod);
    }
  }, [expressDefaultShippingMethod, areShippingMethodsLoading]);
  React.useEffect(() => {
    if (cart?.checkoutState == Models.CheckoutState.Finalized) {
      navigateToCart?.();
    }
  }, [cart?.checkoutState]);
  React.useEffect(() => {
    if (cart) {
      pushCheckoutEventToDataLayer(cart, currency, "express_checkout_paypal");
    }
  }, [cart?.lineItems.length, cart?.id]);
  const handleExpressCheckoutSubmitEvent = React.useCallback(() => {
    cart && pushExpressCheckoutSubmitEventToDataLayer({ cart, paymentMethod: type, isLoggedIn });
  }, [cart, isLoggedIn, type]);
  const onNextClick = async () => {
    dispatch(markFieldsTouched());
    const dateOfBirth = shippingDetails?.expressCheckout.formData["express-checkout-dob"]?.value;
    if (
      // isValid will only check for required, not for valid date
      shippingDetails?.expressCheckout.isEnabled && (!shippingDetails?.expressCheckout.isValid || dateOfBirth && !Util.validations.isDateOfBirth(dateOfBirth))
    ) {
      focusToDOBField();
      toast.failed("checkout.errors.form.general");
      return;
    }
    try {
      handleExpressCheckoutSubmitEvent();
      const paymentData = mapPaymentDataToCustomerDetails(data, locale, country);
      const updatedPaymentData = dateOfBirth && paymentData.customerDetails ? { ...paymentData, customerDetails: { ...paymentData.customerDetails, dateOfBirth } } : paymentData;
      await setCustomerDetails(updatedPaymentData);
      const createOrderDraftPayload = await createOrderDraft({
        paymentDetails: {
          data: {
            referenceId
          },
          isValid: true,
          type: PaymentMethod.PAYPAL
        },
        selectedPaymentMethod: {
          type: PaymentMethod.PAYPAL,
          provider: PaymentServiceProvider.PAYPAL
        },
        returnUrl: window.location.href
      });
      const orderResponse = await createOrder(createOrderDraftPayload);
      redirectToOrderComplete({
        cartId: orderResponse?.cartId || createOrderDraftPayload.checkoutId,
        orderData: orderResponse?.orderDataToken,
        checkoutMethod: "express_checkout_paypal"
      });
    } catch (e) {
      console.error(e);
      toast.failed(CheckoutErrorsMapping.CreateOrderFailed);
    }
  };
  const focusToDOBField = () => {
    if (dobFormRef.current) {
      dobFormRef.current.scrollIntoView({ behavior: "smooth" });
      const inputElement = dobFormRef.current.getElementsByClassName(
        "_is_invalid"
      )[0];
      inputElement?.focus();
    }
  };
  const shippingMethodInfo = getShippingMethodInfo(
    summaryLabels.summary,
    expressDefaultShippingMethod,
    shippingMethodsAsides
  );
  const shippingMethodInfoComponent = () => /* @__PURE__ */ React.createElement(React.Fragment, null, shippingMethodInfo.label, " ", shippingMethodInfo.aside && shippingMethodInfo.aside);
  return /* @__PURE__ */ React.createElement("div", { className: "express-overview-container" }, /* @__PURE__ */ React.createElement(Header, { title: labels.paymentMethod, backButton }), /* @__PURE__ */ React.createElement("section", { className: "express-overview-container__inner" }, /* @__PURE__ */ React.createElement("main", { className: "express-overview-container__main" }, loading && isPaymentDataLoading && /* @__PURE__ */ React.createElement("section", { className: "express-overview-container__loader" }, /* @__PURE__ */ React.createElement(Loader, null)), !loading && !isPaymentDataLoading && cart && /* @__PURE__ */ React.createElement(
    ExpressOverview,
    {
      isUpdating: isCustomerDetailsUpdating || isOrderUpdating,
      onNextClick,
      labels,
      shippingMethodInfo: shippingMethodInfoComponent(),
      addresses,
      contactDetails,
      dobFormRef
    }
  ))));
};
export {
  ExpressOverviewContainer
};
//# sourceMappingURL=ExpressOverviewContainer.js.map
